/**
 * Password recovery client.
 *
 * Both calls are deliberately thin: every decision that matters — whether an
 * account exists, whether a token is live, whether email can be delivered at
 * all — belongs to the server, and repeating any of it here would only create
 * a second answer that can disagree with the first.
 */

/** What the request endpoint told us, in terms the UI can act on. */
export type ResetRequestOutcome =
    /** Accepted. It does NOT mean an account exists — the server answers the same either way. */
    | { kind: "requested" }
    /** Email delivery is not configured. The honest answer is "use support", not "check your inbox". */
    | { kind: "email_unavailable" }
    /** Too many requests for this address recently. */
    | { kind: "throttled" }
    | { kind: "error" };

export async function requestPasswordReset(email: string, locale: string): Promise<ResetRequestOutcome> {
    let res: Response;
    try {
        res = await fetch("/api/auth/password/forgot", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, locale }),
        });
    } catch {
        return { kind: "error" };
    }
    if (res.ok) return { kind: "requested" };
    if (res.status === 429) return { kind: "throttled" };
    if (res.status === 503 || res.status === 404) {
        // 503 covers both "reset is switched off" and "no SMTP configured".
        // 404 is the DEPLOY WINDOW: this site ships web before api, so for a
        // few minutes the page exists and the endpoint does not — and if the
        // route is ever withdrawn, 404 is what the reader gets forever.
        // All three lead to the same place, support, so they are not split in
        // the UI: what matters is that somebody locked out is told to ask a
        // human, not left staring at "something went wrong".
        return { kind: "email_unavailable" };
    }
    return { kind: "error" };
}

export type ResetConfirmOutcome =
    | { kind: "done" }
    /** Unknown, already used, or expired. The server does not distinguish them, and neither do we. */
    | { kind: "token_rejected" }
    /** The password itself was refused (too short, too long). */
    | { kind: "password_rejected"; message?: string }
    | { kind: "error" };

export async function confirmPasswordReset(token: string, newPassword: string): Promise<ResetConfirmOutcome> {
    let res: Response;
    let body: unknown;
    try {
        res = await fetch("/api/auth/password/reset", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token, new_password: newPassword }),
        });
        body = await res.json().catch(() => null);
    } catch {
        return { kind: "error" };
    }
    if (res.ok) return { kind: "done" };
    // 401 is the ONE answer for every unusable token. Telling the reader which
    // way it was unusable would confirm that a real reset existed for a real
    // account, which is what somebody probing a leaked mailbox wants to learn.
    if (res.status === 401) return { kind: "token_rejected" };
    // Same deploy window on the redemption half. A token that cannot be
    // presented is, from the reader's side, a link that does not work — and
    // "ask for a new one" is the right next step either way.
    if (res.status === 404) return { kind: "token_rejected" };
    if (res.status === 400 || res.status === 422) {
        const message = typeof body === "object" && body !== null && "message" in body
            ? String((body as { message?: unknown }).message ?? "")
            : "";
        return { kind: "password_rejected", message: message || undefined };
    }
    return { kind: "error" };
}
